import { ModalsProvider } from '@mantine/modals';
import { Notifications } from '@mantine/notifications';
import { LayoutDashboard } from 'lucide-react';
import { ReactElement, Suspense } from 'react';
import { Navigate, NonIndexRouteObject, Outlet, RouteObject } from 'react-router-dom';

import { FeatureGuard } from '@/components/feature-guard.tsx';
import { RouteErrorBoundary } from '@/components/route-error-boundary.tsx';

import { AppRoute } from '@/constants/app-routes.ts';
import { FeatureFlag } from '@/constants/feature-flags.ts';

import {
    LazyAssistantFormPage,
    LazyAssistantsPage,
    LazyDemoLoginPage,
    LazyExtractionPage,
    LazyMagicLinkPage,
    LazyPlaygroundPage,
    LazyRecallPage,
} from '@/pages/lazy-pages.ts';
import { LoadingPage } from '@/pages/loading-page.tsx';
import { LoginPage } from '@/pages/login-page.tsx';
import { StartPage } from '@/pages/start-page.tsx';

import { AuthenticatedLayout } from './components/authenticated-layout.tsx';

interface EnrichedRoute extends NonIndexRouteObject {
    name?: string;
    key?: string;
    icon?: ReactElement;
}

export const getAuthenticatedRoutes = (): RouteObject[] => {
    const routes: EnrichedRoute[] = [
        {
            path: '',
            element: (
                <RouteErrorBoundary routeName="start">
                    <StartPage />
                </RouteErrorBoundary>
            ),
            name: 'Start',
            key: 'start',
            icon: <LayoutDashboard />,
        },
        {
            path: AppRoute.CAREVIEW,
            // Dashboard rendered persistently in AuthenticatedLayout (keep-alive).
            // The route must exist to match the URL, but it doesn't render anything itself
            // (the empty element prevents a React Router warning about a missing element).
            element: <></>,
        },
        {
            path: AppRoute.PLAYGROUND,
            element: (
                <RouteErrorBoundary routeName="playground">
                    <Suspense fallback={<LoadingPage />}>
                        <LazyPlaygroundPage />
                    </Suspense>
                </RouteErrorBoundary>
            ),
        },
        {
            path: AppRoute.RECALL,
            element: (
                <RouteErrorBoundary routeName="recall">
                    <Suspense fallback={<LoadingPage />}>
                        <FeatureGuard flag={FeatureFlag.IS_RECALL_ENABLED} featureName="Recall">
                            <LazyRecallPage />
                        </FeatureGuard>
                    </Suspense>
                </RouteErrorBoundary>
            ),
        },
        {
            path: AppRoute.EXTRACTION,
            element: (
                <RouteErrorBoundary routeName="extraction">
                    <FeatureGuard flag={FeatureFlag.IS_PATIENTVIEW_ENABLED} featureName="Patientenblick">
                        <Suspense fallback={<LoadingPage />}>
                            <LazyExtractionPage />
                        </Suspense>
                    </FeatureGuard>
                </RouteErrorBoundary>
            ),
        },
        {
            path: AppRoute.CONNECT,
            element: (
                <FeatureGuard flag={FeatureFlag.IS_CONNECT_ENABLED} featureName="Connect">
                    <Outlet />
                </FeatureGuard>
            ),
            children: [
                {
                    index: true,
                    element: (
                        <RouteErrorBoundary routeName="connect">
                            <Suspense fallback={<LoadingPage />}>
                                <LazyAssistantsPage />
                            </Suspense>
                        </RouteErrorBoundary>
                    ),
                },
                {
                    path: AppRoute.ASSISTANT_FORM,
                    element: (
                        <RouteErrorBoundary routeName="assistant-form">
                            <Suspense fallback={<LoadingPage />}>
                                <LazyAssistantFormPage />
                            </Suspense>
                        </RouteErrorBoundary>
                    ),
                },
            ],
        },
        {
            path: AppRoute.FALLBACK,
            element: <Navigate to={AppRoute.ROOT} replace />,
        },
    ];

    return [
        {
            path: AppRoute.ROOT,
            element: (
                <RouteErrorBoundary routeName="root-layout">
                    <ModalsProvider>
                        <Notifications />
                        <AuthenticatedLayout />
                    </ModalsProvider>
                </RouteErrorBoundary>
            ),
            children: routes,
        },
    ];
};

export const unauthenticatedRoutes: EnrichedRoute[] = [
    {
        path: AppRoute.ROOT,
        element: (
            <RouteErrorBoundary routeName="login">
                <LoginPage />
            </RouteErrorBoundary>
        ),
    },
    {
        path: AppRoute.MAGIC_LINK,
        element: (
            <RouteErrorBoundary routeName="magic-link">
                <Suspense fallback={<LoadingPage />}>
                    <LazyMagicLinkPage />
                </Suspense>
            </RouteErrorBoundary>
        ),
    },
    {
        path: AppRoute.DEMO_LOGIN,
        element: (
            <RouteErrorBoundary routeName="demo-login">
                <Suspense fallback={<LoadingPage />}>
                    <LazyDemoLoginPage />
                </Suspense>
            </RouteErrorBoundary>
        ),
    },
    {
        path: AppRoute.FALLBACK,
        element: <Navigate to={AppRoute.ROOT} replace />,
    },
];
